import { ILayerProps } from '@/version-types/layer';
import { ITextLayerProps, TextWritingType } from '@/version-types/layer-props';
import { TextMeasurer } from './measure';

/**
 * 根据图层高度和字体大小和行间距判断是否为横向多行
 * @param layerHeight - 图层高度
 * @param fontSize - 字体大小
 * @param lineSpacing - 行间距
 * @returns
 */
export const checkIsHorizontalMultiLine = (layerHeight: number, fontSize: number, lineSpacing: number) => {
    const ret = layerHeight > fontSize * (lineSpacing + 0.5) ? true : false;
    return ret;
};

export const fixTextSize = function (layer: ILayerProps, fixedDirectionSize = true, loopLevel = 0) {
    const { width, height, data } = layer;
    const { writingMode } = data as ITextLayerProps;

    const result = {
        width: width,
        height: height,
    };

    if (data === null || loopLevel > 100) {
        return result;
    }
    const { width: realWidth, height: realHeight } = TextMeasurer.measure(layer, {
        keepConstantWidth: fixedDirectionSize,
        noWrap: !fixedDirectionSize && writingMode === TextWritingType.HORIZONTAL_TB,
    });

    const { lineSpacing, fontSize } = data as ITextLayerProps;
    const lines = (data as ITextLayerProps).text.split('\n').length;

    if (
        writingMode === TextWritingType.HORIZONTAL_TB &&
        realHeight > lines * lineSpacing * fontSize + (lineSpacing * fontSize) / 2
    ) {
        // 每次进5像素尝试下会不会换行
        const widthStep = 5;
        result.width = width + widthStep;
        result.height = lines * lineSpacing * fontSize;

        // 多次调整宽度
        const { height: h } = TextMeasurer.measure(
            {
                ...layer,
                width: result.width,
                height: result.height,
            },
            {
                keepConstantWidth: fixedDirectionSize,
                noWrap: !fixedDirectionSize && writingMode === TextWritingType.HORIZONTAL_TB,
            },
        );

        if (h > result.height + (lineSpacing * fontSize) / 2 && fontSize !== 0) {
            result.width = fixTextSize(
                {
                    ...layer,
                    width: result.width,
                    height: result.height,
                },
                fixedDirectionSize,
                loopLevel + 1,
            ).width;
        }
    } else if (writingMode === TextWritingType.HORIZONTAL_TB) {
        result.height = realHeight;
        result.width = realWidth;
    } else if (
        writingMode === TextWritingType.VERTICAL_RL &&
        realWidth > lines * lineSpacing * fontSize + (lineSpacing * fontSize) / 2
    ) {
        // 每次进5像素尝试下会不会换行
        const heightStep = 5;
        result.height = height + heightStep;
        result.width = lines * lineSpacing * fontSize;

        // 多次调整高度
        const { width: w } = TextMeasurer.measure(layer, {
            keepConstantWidth: fixedDirectionSize,
        });

        if (w > result.width + (lineSpacing * fontSize) / 2 && fontSize !== 0) {
            result.height = fixTextSize(
                {
                    ...layer,
                    width: result.width,
                    height: result.height,
                },
                fixedDirectionSize,
                loopLevel + 1,
            ).height;
        }
    } else if (writingMode === TextWritingType.VERTICAL_RL) {
        result.height = realHeight;
        result.width = realWidth;
    }

    return result;
};

// 如果是单行文字加半个字
export const oneLineAddHalfSize = (layer: ILayerProps) => {
    const { width, height, data } = layer;

    const result = {
        width: width,
        height: height,
    };

    if (data === null) {
        return result;
    }

    const textData = data as ITextLayerProps;
    const { lineSpacing, fontSize, writingMode } = textData;
    const lines = textData.text.split('\n').length;

    //  有换行符不算单行   图层高度超过行高 + 行高的一半算多行
    if (lines > 1) {
        return result;
    }

    if (
        writingMode === TextWritingType.HORIZONTAL_TB &&
        height >= lineSpacing * fontSize * 2 - (lineSpacing * fontSize) / 2
    ) {
        return result;
    }

    if (
        writingMode === TextWritingType.VERTICAL_RL &&
        width >= lineSpacing * fontSize * 2 - (lineSpacing * fontSize) / 2
    ) {
        return result;
    }

    if (writingMode === TextWritingType.HORIZONTAL_TB) {
        result.width = result.width + fontSize / 2;
    } else {
        result.height = result.height + fontSize / 2;
    }

    return result;
};

/**
 * 检测字符串中是否包含 emoji
 *
 * 使用正则表达式匹配 Unicode 中的 emoji 范围：
 *
 *
 * @param text - 需要检测的文本
 * @returns 是否包含 emoji
 */
export const containsEmoji = (text: string): boolean => {
    const emojiRegex =
        /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
    return emojiRegex.test(text);
};
